import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Award, Download, Clock, Mail, Building2, MapPin, User as UserIcon,
  CheckCircle2, AlertTriangle, AlertCircle, BookOpen, ShieldCheck, Lock, CalendarClock
} from "lucide-react";

function initials(n = "") {
  return n.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}
function avatarColor(n = "") {
  const p = ["#1a3a5c", "#c8232c", "#0f766e", "#7c3aed", "#b45309", "#0369a1", "#be123c", "#4338ca"];
  let h = 0; for (const c of n) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return p[h % p.length];
}
const STATUS = {
  completed: { l: "Concluído", c: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  in_progress: { l: "Em andamento", c: "bg-amber-100 text-amber-700", Icon: Clock },
  pending: { l: "Pendente", c: "bg-slate-100 text-slate-600", Icon: AlertTriangle },
  recycling_required: { l: "Reciclagem", c: "bg-rose-100 text-rose-700", Icon: AlertCircle },
};

export default function CollaboratorDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/collaborators/${id}`).then((r) => setD(r.data)); }, [id]);

  if (!d) return <div className="text-slate-500">Carregando…</div>;
  const u = d.user;

  return (
    <div className="space-y-6" data-testid="collaborator-detail-page">
      <Link to="/dashboard" className="text-sm text-slate-500 hover:text-[var(--unigr-navy)] inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Voltar ao dashboard
      </Link>

      <Card><CardContent className="p-6">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold shrink-0"
               style={{ backgroundColor: avatarColor(u.name) }}>{initials(u.name)}</div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow">Colaborador</div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">{u.name}</h1>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><UserIcon className="w-4 h-4" />{d.position?.name || "—"}</span>
              <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4" />{d.department?.name || "—"}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{d.branch?.name || "—"}</span>
              <span className="flex items-center gap-1.5"><Mail className="w-4 h-4" />{u.email}</span>
              {d.hire_date && (
                <span className="flex items-center gap-1.5" data-testid="hire-date-label">
                  <CalendarClock className="w-4 h-4" />Admissão: {d.hire_date.slice(0, 10)}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-4">
            <Stat label="Cursos ativos" value={`${d.totals.completed_courses}/${d.totals.total_courses}`} />
            <Stat label="A liberar" value={d.totals.upcoming_courses || 0} />
            <Stat label="Horas" value={`${d.totals.hours_done}/${d.totals.hours_planned}h`} />
            <Stat label="Pontos" value={u.points || 0} />
          </div>
        </div>
      </CardContent></Card>

      <section>
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5" />Trilha de aprendizagem — ativos agora ({d.courses.length})</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {d.courses.length === 0 && (
            <Card className="md:col-span-2"><CardContent className="p-6 text-slate-500 text-center">Sem cursos vinculados à trilha do cargo.</CardContent></Card>
          )}
          {d.courses.map((c) => {
            const s = STATUS[c.status] || STATUS.pending;
            const S = s.Icon;
            const highlight = c.cert_status === "expired" ? "border-l-4 border-l-rose-600" : c.cert_status === "expiring_soon" ? "border-l-4 border-l-amber-500" : "";
            return (
              <Card key={c.id} className={"border-[var(--unigr-border)] " + highlight}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-bold">{c.title}</div>
                      <div className="text-xs text-slate-500 mt-1">{c.duration_hours}h · validade {c.validity_months || "—"} meses</div>
                    </div>
                    <span className={"px-2 py-1 rounded text-xs inline-flex items-center gap-1 " + s.c}><S className="w-3 h-3" />{s.l}</span>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">Progresso</span>
                      <span className="font-mono">{c.progress}%</span>
                    </div>
                    <Progress value={c.progress} className="h-2" />
                  </div>
                  {c.cert_code && (
                    <div className="mt-3 text-xs text-slate-600 flex flex-wrap gap-x-3 gap-y-1">
                      <span>Certificado <span className="font-mono">{c.cert_code}</span></span>
                      <span>Nota {c.cert_score}%</span>
                      <span>Emitido {(c.cert_issued_at || "").slice(0, 10)}</span>
                      {c.cert_expires_at && <span>Vence {c.cert_expires_at.slice(0, 10)}</span>}
                      {c.days_left != null && (
                        <span className={c.days_left <= 0 ? "text-rose-600" : c.days_left <= 30 ? "text-amber-600" : "text-emerald-600"}>
                          {c.days_left <= 0 ? "Vencido" : `${c.days_left} dia(s)`}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Link to={`/courses/${c.id}`}><Button size="sm" variant="outline">Ver curso</Button></Link>
                    {c.cert_code && (
                      <a href={`${API}/certificates/${c.cert_code}/pdf`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="btn-navy" data-testid={`dl-cert-${c.cert_code}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {d.upcoming && d.upcoming.length > 0 && (
        <section data-testid="upcoming-track-section">
          <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
            <Lock className="w-5 h-5" />Serão obrigatórios em breve ({d.upcoming.length})
          </h2>
          <p className="text-sm text-slate-500 mb-3">Cursos definidos na trilha do cargo com liberação programada após a data de admissão.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {d.upcoming.map((c) => (
              <Card key={c.id} className="border-[var(--unigr-border)] border-l-4 border-l-slate-300 opacity-90" data-testid={`upcoming-card-${c.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-bold flex items-center gap-2">
                        <Lock className="w-3.5 h-3.5 text-slate-400" />{c.title}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {c.duration_hours}h · validade {c.validity_months || "—"} meses · libera em {c.delay_days} dia(s) após admissão
                      </div>
                    </div>
                    <span className="px-2 py-1 rounded text-xs inline-flex items-center gap-1 bg-slate-100 text-slate-600">
                      <CalendarClock className="w-3 h-3" />Libera em {c.days_until_active} dia(s)
                    </span>
                  </div>
                  <div className="mt-3 text-xs text-slate-500">
                    {c.activates_at && <span>Ativação prevista: <span className="font-mono">{c.activates_at.slice(0, 10)}</span></span>}
                    {!c.mandatory && <Badge className="ml-2 bg-slate-100 text-slate-600 border-0 text-[10px]">recomendado</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><Award className="w-5 h-5" />Todos os certificados emitidos ({d.certificates.length})</h2>
        <Card><CardContent className="p-0">
          {d.certificates.length === 0 ? <div className="p-8 text-center text-slate-500">Sem certificados emitidos.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-2">Curso</th><th className="px-4 py-2">Código</th><th className="px-4 py-2">Nota</th><th className="px-4 py-2">Emitido</th><th className="px-4 py-2"></th></tr>
              </thead>
              <tbody>
                {d.certificates.map((c) => (
                  <tr key={c.id} className="border-t border-[var(--unigr-border)]">
                    <td className="px-4 py-2">{c.course_title}</td>
                    <td className="px-4 py-2 font-mono text-xs">{c.code}</td>
                    <td className="px-4 py-2">{c.score}%</td>
                    <td className="px-4 py-2">{(c.issued_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-2 text-right">
                      <a href={`${API}/certificates/${c.code}/pdf`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline"><Download className="w-3 h-3" /></Button>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent></Card>
      </section>

      <section>
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5" />Ciência de procedimentos ({d.acknowledgements.length})</h2>
        <Card><CardContent className="p-0">
          {d.acknowledgements.length === 0 ? <div className="p-8 text-center text-slate-500">Sem registros de ciência.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-2">Documento</th><th className="px-4 py-2">Versão</th><th className="px-4 py-2">Data</th></tr>
              </thead>
              <tbody>
                {d.acknowledgements.map((a) => (
                  <tr key={a.id} className="border-t border-[var(--unigr-border)]">
                    <td className="px-4 py-2">{a.document_title}</td>
                    <td className="px-4 py-2 font-mono text-xs">{a.version}</td>
                    <td className="px-4 py-2">{(a.timestamp || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent></Card>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-xs text-slate-500 uppercase">{label}</div>
      <div className="font-display text-2xl font-extrabold">{value}</div>
    </div>
  );
}
